import { AllRequestItemFilters, ClientRequestFilterType, FocusedDeliverableId, RequestItemStatusFilterOption } from '../models';
import { ClientRequestGroupStatus, TypedIdentity } from '@aiwyn/common/client-requests';
import {
  ClientRequestDocumentFilesCategorizePayload,
  ClientRequestGroupType,
  ClientRequestSubmitAnswerPayload,
  CreateSecureLinkData,
  LinkFileToDocumentsRequestsPayload,
  SecureLinkSendEmailPayload,
  UpdateSecureLinkGrantsData,
} from '@aiwyn/api';
import { LoadableArrayLoadStrategy, ObjectTraversalPath, ObjectTraversalResult } from '@aiwyn/shared';
import { ClientRequestSubmitFileData } from './client-requests.model';

export class InitializeClientRequests {
  static readonly type = '[ClientRequestsState] InitializeClientRequests';
  constructor(
    public readonly selectedGroupIdentity?: TypedIdentity<ClientRequestGroupType>,
    public readonly selectedRequestId?: string,
  ) {}
}

export class GetSecondaryData {
  static readonly type = '[ClientRequestsState] GetSecondaryData';
  constructor(public readonly loadStrategy?: LoadableArrayLoadStrategy) {}
}

export class UpdateFilter {
  static readonly type = '[ClientRequestsState] UpdateFilter';
  constructor(
    public readonly type: ClientRequestFilterType,
    public readonly value: (number | ClientRequestGroupStatus | RequestItemStatusFilterOption)[],
  ) {}
}

export class SetSelectedClientRequestGroupIdentity {
  static readonly type = '[ClientRequestsState] SetSelectedClientRequestGroupIdentity';
  constructor(public readonly groupIdentity: TypedIdentity<ClientRequestGroupType>) {}
}

export class ClearSelectedClientRequestGroupIdentity {
  static readonly type = '[ClientRequestsState] ClearSelectedClientRequestGroupIdentity';
}

export class SetSelectedClientRequestId {
  static readonly type = '[ClientRequestsState] SetSelectedClientRequestId';
  constructor(
    public readonly requestId: number,
    public readonly requestItemIds?: number[],
  ) {}
}

export class ClearSelectedClientRequestId {
  static readonly type = '[ClientRequestsState] ClearSelectedClientRequestId';
}

export class LoadAnswerActivity {
  static readonly type = '[ClientRequestsState] LoadAnswerActivity';

  constructor(public readonly itemIds: number[]) {}
}

export class LoadDocumentActivity {
  static readonly type = '[ClientRequestsState] LoadDocumentActivity';

  constructor(public readonly itemIds: number[]) {}
}

export class StageFilesForClientRequest {
  static readonly type = '[ClientRequestsState] StageFilesForClientRequest';
  constructor(public readonly jobIds: string[]) {}
}

export class RemoveStagedFilesForClientRequest {
  static readonly type = '[ClientRequestsState] RemoveStagedFilesForClientRequest';
  constructor(public readonly jobIdsToRemove: string[]) {}
}

export class ClearStagedFilesForClientRequest {
  static readonly type = '[ClientRequestsState] ClearStagedFilesForClientRequest';
}

export class SubmitFilesForClientRequest {
  static readonly type = '[ClientRequestsState] SubmitFilesForClientRequest';
  constructor(public readonly files: ClientRequestSubmitFileData[]) {}
}

export class ReplaceFileForClientRequest {
  static readonly type = '[ClientRequestsState] ReplaceFileForClientRequest';
  constructor(
    public readonly clientRequestItemId: number,
    public readonly originalFileId: number,
    public readonly externalDmsFileId: string,
  ) {}
}

export class ReplaceFileForClientRequestSuccess {
  static readonly type = '[ClientRequestsState] ReplaceFileForClientRequestSuccess';
  constructor(
    public readonly clientRequestItemId: number,
    public readonly originalFileId: number,
    public readonly externalDmsFileId: string,
  ) {}
}

export class SubmitClientRequest {
  static readonly type = '[ClientRequestsState] SubmitClientRequest';
}

export class SubmitClientRequestSuccess {
  static readonly type = '[ClientRequestsState] SubmitClientRequestSuccess';
}

export class ClientRequestLinkFilesToDocuments {
  static readonly type = '[ClientRequestsState] ClientRequestLinkFilesToDocuments';
  constructor(public readonly linkFileToDocumentsRequestsPayload: LinkFileToDocumentsRequestsPayload) {}
}

export class CategorizeFileIntoDocument {
  static readonly type = '[ClientRequestsState] CategorizeFileIntoDocument';
  constructor(public readonly clientRequestDocumentFilesCategorizePayload: ClientRequestDocumentFilesCategorizePayload) {}
}

export class UndoFileCategorization {
  static readonly type = '[ClientRequestsState] UndoFileCategorization';
  constructor(public readonly clientRequestDocumentFilesCategorizePayload: ClientRequestDocumentFilesCategorizePayload) {}
}

export class ClientRequestSubmitAnswers {
  static readonly type = '[ClientRequestsState] ClientRequestSubmitAnswers';
  constructor(public readonly submitAnswerPayload: ClientRequestSubmitAnswerPayload) {}
}

export class SetSelectedRequestAsModified {
  static readonly type = '[ClientRequestsState] SetSelectedRequestAsModified';
}

export class SetSelectedRequestItemId {
  static readonly type = '[ClientRequestsState] SetSelectedRequestItemId';

  constructor(public readonly requestItemId: number) {}
}

export class ClearSelectedRequestAsModified {
  static readonly type = '[ClientRequestsState] ClearSelectedRequestAsModified';
}

export class SetFocusedDeliverableId {
  static readonly type = '[ClientRequestsState] SetFocusedDeliverableId';
  constructor(public readonly deliverableId: FocusedDeliverableId) {}
}

export class ClearFocusedDeliverableId {
  static readonly type = '[ClientRequestsState] ClearFocusedDeliverableId';
}
export class AppendNewlyUploadedFiles {
  static readonly type = '[ClientRequestsState] AppendNewlyUploadedFiles';
  constructor(public readonly fileNames: string[]) {}
}

export class RemoveNewlyUploadedFiles {
  static readonly type = '[ClientRequestsState] RemoveNewlyUploadedFiles';
  constructor(public readonly fileNames: string[]) {}
}

export class CreateSecureLink {
  static readonly type = '[ClientRequestsState] CreateSecureLink';

  constructor(public readonly createSecureLinkData: CreateSecureLinkData) {}
}

export class UpdateSecureLinkGrants {
  static readonly type = '[ClientRequestsState] UpdateSecureLinkClaims';

  constructor(public readonly updateSecureLinkGrantsData: UpdateSecureLinkGrantsData) {}
}

export class SecureLinkSendEmail {
  static readonly type = '[ClientRequestsState] SecureLinkSendEmail';

  constructor(public readonly secureLinkSendEmailPayload: SecureLinkSendEmailPayload) {}
}

export class SendClientRequestItemSecureLinkReassignmentNotification {
  static readonly type = '[ClientRequestsState] SendClientRequestItemSecureLinkReassignmentNotification';
}

export class ClientRequestLinkFilesToDocumentsSuccess {
  static readonly type = '[ClientRequestsState] ClientRequestLinkFilesToDocumentsSuccess';
}

export class CategorizeFileIntoDocumentSuccess {
  static readonly type = '[ClientRequestsState] CategorizeFileIntoDocumentSuccess';
}

export class ClientRequestSubmitAnswersSuccess {
  static readonly type = '[ClientRequestsState] ClientRequestSubmitAnswersSuccess';
}

export class UndoFileCategorizationSuccess {
  static readonly type = '[ClientRequestsState] UndoFileCategorizationSuccess';
}

export class SubmitFilesForClientRequestSuccess {
  static readonly type = '[ClientRequestsState] SubmitFilesForClientRequestSuccess';
}

export class AutoCategorizationSuccess {
  static readonly type = '[ClientRequestsState] AutoCategorizationSuccess';
}

export class SetRequestItemAsNotApplicable {
  static readonly type = '[ClientRequestsState] SetRequestItemAsNotApplicable';
  constructor(
    public readonly requestItemId: number,
    public readonly isNotApplicable: boolean,
    public readonly notApplicableReason?: string,
  ) {}
}

export class UpdateRequestItemFilterSelection<
  Path extends ObjectTraversalPath<AllRequestItemFilters> = ObjectTraversalPath<AllRequestItemFilters>,
  Result extends ObjectTraversalResult<AllRequestItemFilters, Path> = ObjectTraversalResult<AllRequestItemFilters, Path>
> {
  static readonly type = '[ClientRequestsState] UpdateRequestItemFilterSelection';
  constructor(public readonly value: Result, public readonly path: Path) {} // can't do spread operator in NGXS... big sadge
}

export class ClearRequestItemFilterSelection {
  static readonly type = '[ClientRequestsState] ClearRequestItemFilterSelection';
  constructor(public readonly requestId: number) {}
}
